import { m } from "framer-motion";
import { ReactNode, createContext, useContext, useEffect } from "react";
import { useLocation } from "wouter";
import { useRouteProgress } from "@/components/RouteProgress";

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
}

// Native-app feel: instant exit (old content snaps away), smooth entrance only.
// The y-offset is deliberately tiny (4px) — just enough to feel intentional, not distracting.
const pageVariants = {
  initial: {
    opacity: 0,
    y: 5,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.22,
      ease: [0.25, 0.46, 0.45, 0.94] as const, // easeOutQuart — snappy, native
    },
  },
  exit: {
    opacity: 0,
    y: 0,
    transition: {
      duration: 0.09, // instant enough to feel native, not zero so AnimatePresence has time to unmount
    },
  },
};

/**
 * Set inside PlatformModal. The modal makes its own entrance, and a section
 * switch there swaps in place as PropPay's does — and a motion wrapper that
 * mounted with the modal was held at its initial opacity (a blank panel), so
 * there the page renders without one.
 */
export const InstantPages = createContext(false);

export function PageTransition({ children, className = "" }: PageTransitionProps) {
  const { complete } = useRouteProgress();
  const instant = useContext(InstantPages);
  const [location] = useLocation();

  /* The page is on screen: finish the route bar. A PASSIVE effect, on purpose
     — the Router starts the bar in a layout effect, and effects run
     child-first, so a layout effect here completed a warm navigation before it
     had started and the bar then ran to its 4s safety net (RouteProgress.tsx).
     Keyed on the location too: a page that stays mounted across a path change
     (Availability's Matrix → Table → Site plan) is still a navigation that
     finished.

     No scrolling here any more. The Router owns it — one instant scroll to
     the top on a forward navigation, the saved position on Back — instead of
     three passes there and a fourth here that landed after a restore. */
  useEffect(() => {
    complete();
  }, [complete, location]);

  if (instant) return <div className={className}>{children}</div>;

  /* No `will-change` here. It used to sit on this wrapper permanently, and
     this wrapper is the WHOLE page: the harness measured it at 94% of the
     viewport on the project picker and 430% (iPad) to 900% (phone) on the
     dashboard, held as one composited layer for as long as the page was open
     — every repaint anywhere in the page repainted tiles of that layer. It
     also made the page the containing block for every `position: fixed`
     descendant (why the dashboard portals its drag overlay to <body>). The
     entrance is 220ms of opacity + transform, which WebKit promotes for the
     animation on its own and drops after; framer ends it at `transform:
     none`, so nothing is left behind. */
  return (
    <m.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      className={className}
    >
      {children}
    </m.div>
  );
}

// Staggered children animation variant
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.04,
    },
  },
};

export const staggerItem = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.18,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

// Scale animation for modals and dialogs
export const scaleVariants = {
  initial: {
    opacity: 0,
    scale: 0.97,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.18,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: {
      duration: 0.1,
    },
  },
};

// Slide animation for sidebar and drawers
export const slideVariants = {
  initial: (direction: "left" | "right" | "up" | "down" = "left") => ({
    opacity: 0,
    x: direction === "left" ? -16 : direction === "right" ? 16 : 0,
    y: direction === "up" ? -16 : direction === "down" ? 16 : 0,
  }),
  animate: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 0.22,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
  exit: (direction: "left" | "right" | "up" | "down" = "left") => ({
    opacity: 0,
    x: direction === "left" ? -16 : direction === "right" ? 16 : 0,
    y: direction === "up" ? -16 : direction === "down" ? 16 : 0,
    transition: {
      duration: 0.1,
    },
  }),
};
